function generateTrendData(days: number, baseValue: number, variance: number, label: string) {
  const data: Record<string, string | number>[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const dayLabel = date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    const value = Math.round((baseValue + (Math.random() - 0.5) * variance * 2) * 10) / 10;
    data.push({ label: dayLabel, [label]: Math.max(0, value) });
  }
  return data;
}

function generateMultiMetricTrends(days: number) {
  const data: Record<string, string | number>[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const dayLabel = date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    const recoveryBase = 65 + Math.sin(i * 0.3) * 10;
    data.push({
      label: dayLabel,
      recovery_score: Math.round((recoveryBase + (Math.random() - 0.5) * 15) * 10) / 10,
      hrv: Math.round((52 + Math.sin(i * 0.25) * 15 + (Math.random() - 0.5) * 10) * 10) / 10,
      resting_heart_rate: Math.round((58 + Math.cos(i * 0.2) * 5 + (Math.random() - 0.5) * 6) * 10) / 10,
      sleep_performance: Math.round((72 + Math.sin(i * 0.35) * 12 + (Math.random() - 0.5) * 10) * 10) / 10,
    });
  }
  return data;
}

function generateHourlyPatterns(baseValue: number, amplitude: number) {
  const data: Record<string, string | number | null>[] = [];
  for (let h = 0; h < 24; h++) {
    const time = `${String(h).padStart(2, "0")}:00`;
    const circadian = Math.sin(((h - 6) / 24) * Math.PI * 2) * amplitude;
    const value = Math.round((baseValue + circadian + (Math.random() - 0.5) * 5) * 10) / 10;
    data.push({ time, value: Math.max(0, value) });
  }
  return data;
}

function generateWeeklyPatterns(metric: string) {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return days.map((day, i) => ({
    day,
    [metric]: Math.round((65 + Math.sin(i * 0.8) * 12 + (Math.random() - 0.5) * 8) * 10) / 10,
  }));
}

function generateSleepStagesTrends(days: number) {
  const data: Record<string, string | number>[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const dayLabel = date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    const deep = 15 + Math.random() * 10;
    const rem = 18 + Math.random() * 8;
    const light = 35 + Math.random() * 10;
    const awake = 5 + Math.random() * 5;
    data.push({
      label: dayLabel,
      sleep_stage_deep: Math.round(deep),
      sleep_stage_rem: Math.round(rem),
      sleep_stage_light: Math.round(light),
      sleep_stage_awake: Math.round(awake),
      sleep_efficiency: Math.round((85 + (Math.random() - 0.5) * 10) * 10) / 10,
      sleep_debt: Math.round((1.2 + (Math.random() - 0.5) * 1.5) * 10) / 10,
      sleep_stress: Math.round((28 + (Math.random() - 0.5) * 20) * 10) / 10,
    });
  }
  return data;
}

function generateStrainTrends(days: number) {
  const data: Record<string, string | number>[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const dayLabel = date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    data.push({
      label: dayLabel,
      day_strain: Math.round((10.5 + (Math.random() - 0.5) * 8) * 10) / 10,
      cardio_load: Math.round(120 + (Math.random() - 0.5) * 80),
      heart_rate: Math.round(72 + (Math.random() - 0.5) * 20),
      calories_burn: Math.round(2200 + (Math.random() - 0.5) * 600),
    });
  }
  return data;
}

function generateStressTrends(days: number) {
  const data: Record<string, string | number>[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const dayLabel = date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    data.push({
      label: dayLabel,
      stress_score: Math.round((2.8 + Math.sin(i * 0.3) * 1.2 + (Math.random() - 0.5) * 1) * 10) / 10,
      hrv: Math.round((55 + Math.sin(i * 0.25) * 15 + (Math.random() - 0.5) * 10) * 10) / 10,
      stress_recovery_balance: Math.round((58 + Math.sin(i * 0.4) * 18 + (Math.random() - 0.5) * 10) * 10) / 10,
    });
  }
  return data;
}

function generateBiometricsTrends(days: number) {
  const data: Record<string, string | number>[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const dayLabel = date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    data.push({
      label: dayLabel,
      avg_heart_rate: Math.round(72 + (Math.random() - 0.5) * 12),
      max_heart_rate: Math.round(145 + (Math.random() - 0.5) * 30),
      hrv: Math.round((52 + Math.sin(i * 0.25) * 12 + (Math.random() - 0.5) * 8) * 10) / 10,
      resting_heart_rate: Math.round((57 + Math.cos(i * 0.2) * 4 + (Math.random() - 0.5) * 5) * 10) / 10,
      respiratory_rate: Math.round((15.5 + (Math.random() - 0.5) * 3) * 10) / 10,
      spo2: Math.round((97.2 + (Math.random() - 0.5) * 2) * 10) / 10,
    });
  }
  return data;
}

export const recoveryStats = {
  recovery_score: { label: "Recovery Score", value: 68, unit: "%", change: "+5.2%", previous_period_value: 64.6, rag_label: "Good", rag_color: "green", rag_insight: "Recovery is in a healthy range" },
  hrv: { label: "HRV", value: 52, unit: "ms", change: "+3.1%", previous_period_value: 50.4, rag_label: "Fair", rag_color: "amber", rag_insight: "HRV is moderate" },
  resting_heart_rate: { label: "Resting Heart Rate", value: 58, unit: "bpm", change: "-1.8%", previous_period_value: 59.1, rag_label: "Good", rag_color: "green", rag_insight: "Resting HR is within optimal range" },
  sleep_performance: { label: "Sleep Performance", value: 74, unit: "%", change: "+2.4%", previous_period_value: 72.3, rag_label: "Good", rag_color: "green", rag_insight: "Sleep quality is good" },
};

export const recoveryTrends = generateMultiMetricTrends(30);

export const recoveryDistribution = [
  { label: "67-100 (High)", value: 42, color: undefined as string | undefined },
  { label: "34-66 (Medium)", value: 35, color: undefined as string | undefined },
  { label: "0-33 (Low)", value: 23, color: undefined as string | undefined },
];

export const heartRatePatterns = generateHourlyPatterns(72, 15);

export const sleepStats = {
  sleep_performance: { label: "Sleep Performance", value: 74, unit: "%", change: "+2.4%", previous_period_value: 72.3, rag_label: "Good", rag_color: "green", rag_insight: "Sleep quality is good" },
  sleep_consistency: { label: "Sleep Consistency", value: 81, unit: "%", change: "+1.2%", previous_period_value: 80, rag_label: "Good", rag_color: "green", rag_insight: "Sleep schedule is consistent" },
  sleep_efficiency: { label: "Sleep Efficiency", value: 88, unit: "%", change: "+0.8%", previous_period_value: 87.3, rag_label: "Good", rag_color: "green", rag_insight: "High sleep efficiency" },
  high_sleep_stress: { label: "High Sleep Stress Days", value: 4, unit: null, change: "-12.5%", previous_period_value: 4.6, rag_label: "Fair", rag_color: "amber", rag_insight: "Some elevated sleep stress days" },
};

export const sleepTrends = generateSleepStagesTrends(30);

export const sleepStageDistribution = [
  { label: "Deep", value: 18, color: undefined as string | undefined },
  { label: "REM", value: 22, color: undefined as string | undefined },
  { label: "Light", value: 42, color: undefined as string | undefined },
  { label: "Awake", value: 8, color: undefined as string | undefined },
];

export const sleepHrPatterns = [
  { day: "Mon", heart_rate: 58, sleep_efficiency: 87 },
  { day: "Tue", heart_rate: 56, sleep_efficiency: 89 },
  { day: "Wed", heart_rate: 59, sleep_efficiency: 85 },
  { day: "Thu", heart_rate: 57, sleep_efficiency: 88 },
  { day: "Fri", heart_rate: 61, sleep_efficiency: 82 },
  { day: "Sat", heart_rate: 55, sleep_efficiency: 91 },
  { day: "Sun", heart_rate: 54, sleep_efficiency: 90 },
];

export const strainStats = {
  day_strain: { label: "Day Strain", value: 10.8, unit: null, change: "+4.2%", previous_period_value: 10.4, rag_label: "Good", rag_color: "green", rag_insight: "Strain is moderate" },
  active_time: { label: "Active Time", value: 142, unit: "min", change: "+8.1%", previous_period_value: 131, rag_label: "Good", rag_color: "green", rag_insight: "Good activity levels" },
  calories_burn: { label: "Calories Burned", value: 2340, unit: "kcal", change: "+3.5%", previous_period_value: 2261, rag_label: "Good", rag_color: "green", rag_insight: "Healthy calorie expenditure" },
};

export const strainTrends = generateStrainTrends(30);

export const heartRateZonesDistribution = [
  { label: "Zone 1 (50-60%)", value: 32, color: undefined as string | undefined },
  { label: "Zone 2 (60-70%)", value: 28, color: undefined as string | undefined },
  { label: "Zone 3 (70-80%)", value: 22, color: undefined as string | undefined },
  { label: "Zone 4 (80-90%)", value: 12, color: undefined as string | undefined },
  { label: "Zone 5 (90-100%)", value: 6, color: undefined as string | undefined },
];

export const strainRecoveryPatterns = [
  { day: "Mon", day_strain: 12.5, recovery_pct: 62 },
  { day: "Tue", day_strain: 8.2, recovery_pct: 71 },
  { day: "Wed", day_strain: 14.1, recovery_pct: 55 },
  { day: "Thu", day_strain: 9.8, recovery_pct: 68 },
  { day: "Fri", day_strain: 11.3, recovery_pct: 64 },
  { day: "Sat", day_strain: 6.5, recovery_pct: 78 },
  { day: "Sun", day_strain: 5.2, recovery_pct: 82 },
];

export const stressStats = {
  stress_score: { label: "Stress Score", value: 2.8, unit: null, change: "+6.1%", previous_period_value: 2.64, rag_label: "Fair", rag_color: "amber", rag_insight: "Moderate physiological stress" },
  hrv: { label: "HRV", value: 52, unit: "ms", change: "+3.1%", previous_period_value: 50.4, rag_label: "Fair", rag_color: "amber", rag_insight: "HRV is moderate" },
  high_stress_days: { label: "High Stress Days", value: 6, unit: null, change: "-8.3%", previous_period_value: 6.5, rag_label: "Fair", rag_color: "amber", rag_insight: "Some elevated stress days" },
  stress_recovery_balance: { label: "Stress-Recovery Balance", value: 58, unit: "%", change: "+4.5%", previous_period_value: 55.5, rag_label: "Fair", rag_color: "amber", rag_insight: "Balance improving" },
};

export const stressTrends = generateStressTrends(30);

export const stressDistribution = [
  { label: "Low Stress", value: 38, color: undefined as string | undefined },
  { label: "Moderate Stress", value: 42, color: undefined as string | undefined },
  { label: "High Stress", value: 20, color: undefined as string | undefined },
];

export const stressCombinedPatterns = [
  { day: "Mon", stress_score: 3.2, hrv: 48 },
  { day: "Tue", stress_score: 2.6, hrv: 55 },
  { day: "Wed", stress_score: 3.5, hrv: 44 },
  { day: "Thu", stress_score: 2.9, hrv: 51 },
  { day: "Fri", stress_score: 3.1, hrv: 49 },
  { day: "Sat", stress_score: 2.1, hrv: 60 },
  { day: "Sun", stress_score: 1.8, hrv: 63 },
];

export const biometricsStats = {
  resting_heart_rate: { label: "Resting Heart Rate", value: 58, unit: "bpm", change: "-1.8%", previous_period_value: 59.1, rag_label: "Good", rag_color: "green", rag_insight: "Resting HR is within optimal range" },
  hrv: { label: "HRV", value: 52, unit: "ms", change: "+3.1%", previous_period_value: 50.4, rag_label: "Fair", rag_color: "amber", rag_insight: "HRV is moderate" },
  respiratory_rate: { label: "Respiratory Rate", value: 15.5, unit: "brpm", change: "+0.6%", previous_period_value: 15.4, rag_label: "Good", rag_color: "green", rag_insight: "Within healthy range (12-20 brpm)" },
  spo2: { label: "SpO2", value: 97.2, unit: "%", change: "+0.1%", previous_period_value: 97.1, rag_label: "Good", rag_color: "green", rag_insight: "Healthy blood oxygen saturation" },
};

export const biometricsTrends = generateBiometricsTrends(30);

export const respiratoryPatterns = generateHourlyPatterns(15.5, 2);

export const skinTempPatterns = generateHourlyPatterns(36.5, 0.8);

export const riskBreakdown = { high: 8, medium: 18, low: 74 };
export const riskTotal = 120;
export const riskParticipationRate = 83;

export const radarComparison = [
  { metric: "Recovery", current: 68, previous: 62 },
  { metric: "Sleep", current: 74, previous: 70 },
  { metric: "Strain", current: 55, previous: 52 },
  { metric: "HRV", current: 65, previous: 58 },
  { metric: "Stress", current: 42, previous: 48 },
  { metric: "Activity", current: 71, previous: 65 },
];

export const gaugeData = {
  overallWellbeing: 62,
  recoveryReadiness: 68,
};

// ── Programme Profile ──────────────────────────────────────

export const programmeStats = {
  total_participants: { label: "Total Participants", value: 120, unit: null, change: "+12.5%", previous_period_value: 107, rag_label: "Good", rag_color: "green", rag_insight: "Growing enrolment" },
  active_participants: { label: "Active Participants", value: 98, unit: null, change: "+8.2%", previous_period_value: 91, rag_label: "Good", rag_color: "green", rag_insight: "Strong engagement" },
  avg_participation: { label: "Avg Participation Rate", value: 83, unit: "%", change: "+3.4%", previous_period_value: 80.3, rag_label: "Good", rag_color: "green", rag_insight: "Above target" },
  device_connection: { label: "Device Connection Rate", value: 91, unit: "%", change: "+2.1%", previous_period_value: 89.1, rag_label: "Good", rag_color: "green", rag_insight: "Most users connected" },
};

export const demographicsData = [
  { label: "18-25", value: 15 },
  { label: "26-35", value: 32 },
  { label: "36-45", value: 28 },
  { label: "46-55", value: 22 },
  { label: "56-65", value: 18 },
  { label: "65+", value: 5 },
];

export const genderDistribution = [
  { label: "Male", value: 58, color: undefined as string | undefined },
  { label: "Female", value: 38, color: undefined as string | undefined },
  { label: "Other", value: 4, color: undefined as string | undefined },
];

export const departmentDistribution = [
  { label: "Engineering", value: 32, color: undefined as string | undefined },
  { label: "Operations", value: 28, color: undefined as string | undefined },
  { label: "Sales", value: 22, color: undefined as string | undefined },
  { label: "HR", value: 18, color: undefined as string | undefined },
  { label: "Finance", value: 12, color: undefined as string | undefined },
  { label: "Marketing", value: 8, color: undefined as string | undefined },
];

export const shiftPatternDistribution = [
  { label: "Day Shift", value: 65, color: undefined as string | undefined },
  { label: "Night Shift", value: 22, color: undefined as string | undefined },
  { label: "Rotating", value: 18, color: undefined as string | undefined },
  { label: "Flexible", value: 15, color: undefined as string | undefined },
];

export const travelTimeDistribution = [
  { label: "0-15 min", value: 28 },
  { label: "15-30 min", value: 35 },
  { label: "30-45 min", value: 22 },
  { label: "45-60 min", value: 10 },
  { label: "60+ min", value: 5 },
];

// ── Wellness Overview ──────────────────────────────────────

export const wellnessOverviewStats = {
  overall_wellness: { label: "Overall Wellness", value: 72, unit: "%", change: "+4.1%", previous_period_value: 69.2, rag_label: "Good", rag_color: "green", rag_insight: "Wellness trending positively" },
  avg_recovery: { label: "Avg Recovery", value: 68, unit: "%", change: "+5.2%", previous_period_value: 64.6, rag_label: "Good", rag_color: "green", rag_insight: "Recovery is solid" },
  avg_sleep_score: { label: "Avg Sleep Score", value: 74, unit: "%", change: "+2.4%", previous_period_value: 72.3, rag_label: "Good", rag_color: "green", rag_insight: "Sleep quality is good" },
};

export const wellnessRadar = [
  { metric: "Recovery", value: 68, benchmark: 65 },
  { metric: "Sleep", value: 74, benchmark: 70 },
  { metric: "Strain", value: 55, benchmark: 58 },
  { metric: "HRV", value: 65, benchmark: 60 },
  { metric: "Stress", value: 42, benchmark: 45 },
  { metric: "Activity", value: 71, benchmark: 68 },
];

export const departmentPerformance = [
  { department: "Engineering", recovery: 72, sleep: 76, strain: 58, wellness: 75 },
  { department: "Operations", recovery: 64, sleep: 70, strain: 62, wellness: 68 },
  { department: "Sales", recovery: 66, sleep: 68, strain: 55, wellness: 70 },
  { department: "HR", recovery: 70, sleep: 78, strain: 48, wellness: 74 },
  { department: "Finance", recovery: 68, sleep: 72, strain: 50, wellness: 71 },
  { department: "Marketing", recovery: 71, sleep: 75, strain: 52, wellness: 73 },
];

export const weeklyWorkoutMinutes = [
  { day: "Mon", minutes: 42 },
  { day: "Tue", minutes: 55 },
  { day: "Wed", minutes: 38 },
  { day: "Thu", minutes: 48 },
  { day: "Fri", minutes: 35 },
  { day: "Sat", minutes: 62 },
  { day: "Sun", minutes: 28 },
];

// ── User Progress ──────────────────────────────────────────

export const sampleUsers = [
  { id: "u1", name: "Sarah Mitchell", department: "Engineering", role: "Senior Engineer" },
  { id: "u2", name: "James Chen", department: "Operations", role: "Team Lead" },
  { id: "u3", name: "Emma Thompson", department: "Sales", role: "Account Manager" },
  { id: "u4", name: "Michael Brown", department: "HR", role: "HR Specialist" },
  { id: "u5", name: "Olivia Williams", department: "Finance", role: "Financial Analyst" },
  { id: "u6", name: "Daniel Garcia", department: "Engineering", role: "DevOps Engineer" },
  { id: "u7", name: "Sophie Taylor", department: "Marketing", role: "Content Lead" },
  { id: "u8", name: "Alex Johnson", department: "Operations", role: "Operations Manager" },
];

export const userProgressStats = {
  recovery_score: { label: "Recovery Score", value: 72, unit: "%", change: "+6.3%", previous_period_value: 67.7, rag_label: "Good", rag_color: "green", rag_insight: "Above average recovery" },
  sleep_performance: { label: "Sleep Performance", value: 78, unit: "%", change: "+3.8%", previous_period_value: 75.1, rag_label: "Good", rag_color: "green", rag_insight: "Good sleep quality" },
  day_strain: { label: "Day Strain", value: 11.2, unit: null, change: "+2.1%", previous_period_value: 11.0, rag_label: "Good", rag_color: "green", rag_insight: "Moderate strain" },
  hrv: { label: "HRV", value: 56, unit: "ms", change: "+4.5%", previous_period_value: 53.6, rag_label: "Good", rag_color: "green", rag_insight: "Good variability" },
  resting_heart_rate: { label: "Resting Heart Rate", value: 56, unit: "bpm", change: "-2.3%", previous_period_value: 57.3, rag_label: "Good", rag_color: "green", rag_insight: "Healthy resting HR" },
  stress_score: { label: "Stress Score", value: 2.4, unit: null, change: "-8.2%", previous_period_value: 2.6, rag_label: "Good", rag_color: "green", rag_insight: "Low stress levels" },
  sleep_efficiency: { label: "Sleep Efficiency", value: 90, unit: "%", change: "+1.2%", previous_period_value: 88.9, rag_label: "Good", rag_color: "green", rag_insight: "Excellent efficiency" },
  calories_burn: { label: "Calories Burned", value: 2480, unit: "kcal", change: "+5.1%", previous_period_value: 2360, rag_label: "Good", rag_color: "green", rag_insight: "Active lifestyle" },
};

export const userWellnessDimensions = [
  { metric: "Physical", value: 75, benchmark: 70 },
  { metric: "Sleep", value: 78, benchmark: 72 },
  { metric: "Recovery", value: 72, benchmark: 68 },
  { metric: "Mental", value: 68, benchmark: 65 },
  { metric: "Activity", value: 80, benchmark: 72 },
];

export const userAiInsights = [
  { type: "highlight" as const, title: "Strong Recovery Pattern", description: "Recovery scores have consistently improved over the past 2 weeks, suggesting effective rest and workload management." },
  { type: "recommendation" as const, title: "Optimise Sleep Timing", description: "Data suggests shifting bedtime 30 minutes earlier could improve deep sleep by approximately 12%, based on circadian rhythm patterns." },
  { type: "watch" as const, title: "Weekend Strain Dip", description: "Activity levels drop significantly on weekends. Consider light weekend activity to maintain cardiovascular fitness continuity." },
];

// ── Team Progress ──────────────────────────────────────────

export const sampleTeams = [
  { id: "t1", name: "Engineering", members: 32 },
  { id: "t2", name: "Operations", members: 28 },
  { id: "t3", name: "Sales", members: 22 },
  { id: "t4", name: "HR", members: 18 },
  { id: "t5", name: "Finance", members: 12 },
  { id: "t6", name: "Marketing", members: 8 },
];

export const teamProgressStats = {
  recovery_score: { label: "Team Avg Recovery", value: 69, unit: "%", change: "+4.8%", previous_period_value: 65.8, rag_label: "Good", rag_color: "green", rag_insight: "Team recovery improving" },
  sleep_performance: { label: "Team Avg Sleep", value: 73, unit: "%", change: "+2.9%", previous_period_value: 70.9, rag_label: "Good", rag_color: "green", rag_insight: "Consistent sleep quality" },
  day_strain: { label: "Team Avg Strain", value: 10.5, unit: null, change: "+3.2%", previous_period_value: 10.2, rag_label: "Good", rag_color: "green", rag_insight: "Moderate activity" },
  hrv: { label: "Team Avg HRV", value: 51, unit: "ms", change: "+2.8%", previous_period_value: 49.6, rag_label: "Fair", rag_color: "amber", rag_insight: "Room for improvement" },
  resting_heart_rate: { label: "Team Avg Resting HR", value: 59, unit: "bpm", change: "-1.5%", previous_period_value: 59.9, rag_label: "Good", rag_color: "green", rag_insight: "Healthy range" },
  stress_score: { label: "Team Avg Stress", value: 2.9, unit: null, change: "-5.1%", previous_period_value: 3.1, rag_label: "Fair", rag_color: "amber", rag_insight: "Moderate stress" },
  sleep_efficiency: { label: "Team Avg Efficiency", value: 87, unit: "%", change: "+0.9%", previous_period_value: 86.2, rag_label: "Good", rag_color: "green", rag_insight: "Good efficiency" },
  participation: { label: "Participation Rate", value: 83, unit: "%", change: "+3.4%", previous_period_value: 80.3, rag_label: "Good", rag_color: "green", rag_insight: "Strong engagement" },
};

export const teamAiInsights = [
  { type: "highlight" as const, title: "Team Recovery Uptrend", description: "Engineering department shows a 12% improvement in recovery scores over the past month, correlating with reduced overtime hours." },
  { type: "recommendation" as const, title: "Address Night Shift Sleep", description: "Operations night shift workers show 18% lower sleep efficiency. Consider implementing sleep hygiene workshops for this cohort." },
  { type: "watch" as const, title: "Sales Team Stress Elevation", description: "Sales team stress scores are trending upward (+15% this quarter). May correlate with end-of-quarter targets. Monitor closely." },
];

// ── Predictive Insights ────────────────────────────────────

export const predictiveOverallScore = 72;

export const predictiveHighlights = [
  { title: "Recovery Trajectory", description: "Based on current trends, overall recovery scores are projected to increase by 8% over the next 4 weeks. Key driver: improved sleep consistency across Engineering and Operations teams.", impact: "positive" as const },
  { title: "Seasonal Strain Pattern", description: "Historical data suggests a 12% increase in average strain levels expected in the coming month, consistent with seasonal activity patterns observed in previous years.", impact: "neutral" as const },
  { title: "HRV Improvement Potential", description: "AI analysis indicates that 35% of participants have HRV patterns suggesting significant improvement potential through targeted breathing exercises and stress management.", impact: "positive" as const },
];

export const predictiveRecommendations = [
  { priority: "high" as const, title: "Sleep Intervention for Night Shift", description: "Predictive models suggest targeted sleep hygiene training for night shift workers could improve overall wellness scores by 6-8% within 6 weeks.", metric: "Sleep Performance", projected_improvement: "+7.2%" },
  { priority: "medium" as const, title: "Recovery Day Scheduling", description: "Data patterns indicate scheduling recovery days after high-strain periods could reduce burnout risk by 22% across all departments.", metric: "Recovery Score", projected_improvement: "+5.5%" },
  { priority: "medium" as const, title: "Activity Optimisation", description: "Current activity distribution shows 40% of strain occurring in Zone 4-5. Redistributing to more Zone 2-3 activity could improve cardiovascular efficiency.", metric: "Cardio Efficiency", projected_improvement: "+4.8%" },
];

export const predictiveAreasToWatch = [
  { severity: "amber" as const, title: "Q2 Stress Spike Risk", description: "Historical models predict a 15-20% increase in stress scores during the upcoming quarter transition. Proactive workload management recommended.", affected_teams: ["Sales", "Operations"] },
  { severity: "amber" as const, title: "Weekend Recovery Gap", description: "Weekend recovery patterns show a widening gap between weekday and weekend scores. If uncorrected, this could lead to accumulated fatigue.", affected_teams: ["Engineering", "Marketing"] },
  { severity: "red" as const, title: "Cardiovascular Risk Cohort", description: "8 participants show sustained low HRV (<20ms) combined with elevated resting HR (>75bpm). Individual interventions recommended.", affected_teams: ["Operations", "Sales"] },
];

// ── Measure Comparison ─────────────────────────────────────

export const comparisonMetrics = [
  "Recovery Score", "HRV", "Resting Heart Rate", "Sleep Performance",
  "Sleep Efficiency", "Day Strain", "Calories Burned", "Stress Score",
  "Sleep Debt", "Respiratory Rate", "SpO2",
];

export const comparisonTrends = generateMultiMetricTrends(30);

export const comparisonByDepartment = [
  { department: "Engineering", recovery_score: 72, hrv: 55, sleep_performance: 76, day_strain: 11.2 },
  { department: "Operations", recovery_score: 64, hrv: 48, sleep_performance: 70, day_strain: 13.5 },
  { department: "Sales", recovery_score: 66, hrv: 50, sleep_performance: 68, day_strain: 9.8 },
  { department: "HR", recovery_score: 70, hrv: 53, sleep_performance: 78, day_strain: 8.5 },
  { department: "Finance", recovery_score: 68, hrv: 52, sleep_performance: 72, day_strain: 7.2 },
  { department: "Marketing", recovery_score: 71, hrv: 54, sleep_performance: 75, day_strain: 8.8 },
];

export const correlationData = [
  { recovery_score: 72, hrv: 55, sleep_performance: 76 },
  { recovery_score: 64, hrv: 48, sleep_performance: 70 },
  { recovery_score: 78, hrv: 62, sleep_performance: 82 },
  { recovery_score: 55, hrv: 42, sleep_performance: 65 },
  { recovery_score: 68, hrv: 50, sleep_performance: 72 },
  { recovery_score: 82, hrv: 65, sleep_performance: 85 },
  { recovery_score: 60, hrv: 45, sleep_performance: 68 },
  { recovery_score: 75, hrv: 58, sleep_performance: 78 },
  { recovery_score: 70, hrv: 52, sleep_performance: 74 },
  { recovery_score: 62, hrv: 46, sleep_performance: 66 },
];

// ── Jeopardy Insights – Table Data ────────────────────────

export const atRiskUsers = [
  { name: "Tom Richards", status: "Active", risk_level: "High", risk_score: 82, recovery: 35, sleep: 42, strain: 18.5, hrv: 18, days_in_red: 12 },
  { name: "Karen Mills", status: "Active", risk_level: "High", risk_score: 78, recovery: 38, sleep: 45, strain: 17.2, hrv: 22, days_in_red: 10 },
  { name: "David Park", status: "Active", risk_level: "High", risk_score: 75, recovery: 40, sleep: 48, strain: 16.8, hrv: 24, days_in_red: 9 },
  { name: "Lisa Johnson", status: "Active", risk_level: "Medium", risk_score: 62, recovery: 52, sleep: 58, strain: 14.2, hrv: 32, days_in_red: 5 },
  { name: "Robert Chen", status: "Active", risk_level: "Medium", risk_score: 58, recovery: 55, sleep: 60, strain: 13.5, hrv: 35, days_in_red: 4 },
  { name: "Amy Foster", status: "Active", risk_level: "Medium", risk_score: 55, recovery: 58, sleep: 62, strain: 12.8, hrv: 38, days_in_red: 3 },
  { name: "Mark Wilson", status: "Active", risk_level: "Medium", risk_score: 52, recovery: 60, sleep: 64, strain: 12.1, hrv: 40, days_in_red: 3 },
  { name: "Jessica Lee", status: "Active", risk_level: "Low", risk_score: 28, recovery: 72, sleep: 78, strain: 10.5, hrv: 52, days_in_red: 0 },
  { name: "Chris Evans", status: "Active", risk_level: "Low", risk_score: 22, recovery: 75, sleep: 80, strain: 9.8, hrv: 55, days_in_red: 0 },
  { name: "Rachel Adams", status: "Active", risk_level: "Low", risk_score: 18, recovery: 78, sleep: 82, strain: 8.5, hrv: 58, days_in_red: 0 },
];

export const cardiovascularJeopardyUsers = [
  { name: "Tom Richards", department: "Operations", avg_hrv: 18, avg_resting_hr: 82, status: "red" as const },
  { name: "Karen Mills", department: "Sales", avg_hrv: 22, avg_resting_hr: 78, status: "red" as const },
  { name: "David Park", department: "Operations", avg_hrv: 19, avg_resting_hr: 81, status: "red" as const },
  { name: "Robert Chen", department: "Engineering", avg_hrv: 28, avg_resting_hr: 72, status: "amber" as const },
  { name: "Lisa Johnson", department: "HR", avg_hrv: 30, avg_resting_hr: 74, status: "amber" as const },
  { name: "Mark Wilson", department: "Finance", avg_hrv: 32, avg_resting_hr: 70, status: "amber" as const },
];

// ── Management Dashboard ───────────────────────────────────

export const managementStats = {
  total_users: { label: "Total Users", value: 120, unit: null, change: "+12.5%", previous_period_value: 107, rag_label: "Good", rag_color: "green", rag_insight: "Growing user base" },
  active_users: { label: "Active Users", value: 98, unit: null, change: "+8.2%", previous_period_value: 91, rag_label: "Good", rag_color: "green", rag_insight: "Strong daily engagement" },
  pending_invites: { label: "Pending Invites", value: 14, unit: null, change: "-22.2%", previous_period_value: 18, rag_label: "Good", rag_color: "green", rag_insight: "Invites being accepted" },
  device_connected: { label: "Devices Connected", value: 109, unit: null, change: "+6.8%", previous_period_value: 102, rag_label: "Good", rag_color: "green", rag_insight: "High device adoption" },
};

export const recentActivity = [
  { user: "Sarah Mitchell", action: "Connected Whoop device", time: "2 hours ago" },
  { user: "James Chen", action: "Completed onboarding assessment", time: "4 hours ago" },
  { user: "Emma Thompson", action: "Accepted invite", time: "Yesterday" },
  { user: "Michael Brown", action: "Updated profile", time: "Yesterday" },
  { user: "Olivia Williams", action: "Connected Fitbit device", time: "2 days ago" },
  { user: "Daniel Garcia", action: "Completed onboarding assessment", time: "2 days ago" },
  { user: "Sophie Taylor", action: "Accepted invite", time: "3 days ago" },
  { user: "Alex Johnson", action: "Connected Whoop device", time: "3 days ago" },
];
